import { Chip } from '@material-ui/core'
import Slide from '@material-ui/core/Slide'
import React, {
    MutableRefObject,
    ReactElement,
    ReactNode,
    RefObject,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { FieldsList as FieldsListLanguage } from '../../../../i18n'
import LanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import Checkbox from '../../Common/Components/Checkbox'
import Heading from '../../Common/Components/Heading'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'
import Icon, { IconName } from '../../Common/Components/Icon'
import IconButton, { IconButtonType } from '../../Common/Components/IconButton'
import Ul, { Li } from '../../Common/Components/Ul'
import { COMMA, DASH, EMPTY_STRING, SLASH, SPACE } from '../../Common/Constants/Separators'
import { REPORT_STAT_FLAGS_TYPE_NONE } from '../../Common/Constants/ReportStatFlags'
import { LanguageContext, ReactState } from '../../Common/Contexts'
import { RptFilterEntityInfo } from '../../Common/Types/Types'
import { AddMenuItem, buildMenuStructureFromAvailableStats } from './BuildAddMenuUtils'
import { AvailableStatisticsFieldsArray, StatisticsConfiguration, StatProps } from './Types'
import { ReportTypes } from '../../../NonReact/constants/ReportTypes'
import { ENTITY_STAT_FIXED_FIELDS } from '../Constants'
import { ArrayUtils } from '../../../NonReact/utils/ArrayUtils'

const MUI_POPOVER_PAPER_CLASS_SELECTOR = '.MuiPopover-paper'
const FIELDS_ADD_BUTTON_SELECTOR = '.fields-add-button'
const POPOVER_MARGIN_THRESHOLD_PX = 16
const POPOVER_VERTICAL_OFFSET_PX = 25

type PendingNav = {
    subItems: AddMenuItem[]
    name: string
    isBack: boolean
}

export type FieldsAddProps = {
    statsList: AvailableStatisticsFieldsArray
    configuredStatisticsConfigurations: StatisticsConfiguration[]
    addStatistic: (statistic: StatProps) => void
    removeStatistic: (statistic: StatProps) => void
    rptFilterEntityInfo: Definable<RptFilterEntityInfo>
    isUserLicensedForCurrentReportStyle: boolean
    selectedReportStyle: Definable<ReportTypes>
    anchorRef?: React.RefObject<HTMLElement>
}

const SlideDirectionType = { left: 'left', right: 'right' } as const
type SlideDirectionType = (typeof SlideDirectionType)[keyof typeof SlideDirectionType]

/**
 * Fields tab in report configuration modal window.
 *
 * @param statsList List of all statistics available.
 * @param configuredStatisticsConfigurations List of currently selected statistics.
 * @param addStatistic Callback metod to add new statistics to list of selected statistics.
 * @param removeStatistic Callback method to remove statistics from list of selected statistics.
 * @param rptFilterEntityInfo Up-to-date report filter entity info object containing report capabilities.
 * @param isUserLicensedForCurrentReportStyle Flag specifying whether user is licensed for current report style.
 */
const FieldsListAdd: React.FC<FieldsAddProps> = ({
    statsList,
    configuredStatisticsConfigurations,
    addStatistic,
    removeStatistic,
    rptFilterEntityInfo,
    isUserLicensedForCurrentReportStyle,
    selectedReportStyle,
    anchorRef
}): ReactElement => {
    const [menuStack, setMenuStack]: ReactState<AddMenuItem[][]> = useState<AddMenuItem[][]>([])
    const [breadcrumb, setBreadcrumb]: ReactState<string[]> = useState<string[]>([])
    const [slideIn, setSlideIn]: ReactState<boolean> = useState<boolean>(true)
    const [slideDirection, setSlideDirection]: ReactState<SlideDirectionType> = useState<SlideDirectionType>(
        SlideDirectionType.left
    )
    const pendingNavRef: MutableRefObject<Nullable<PendingNav>> = useRef<Nullable<PendingNav>>(null)
    const rootRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    const language = useContext(LanguageContext) as LanguageHandler
    language.loadBundle(FieldsListLanguage.NAME)

    const repositionPaper = useCallback((): void => {
        requestAnimationFrame((): void => {
            const paper: Nullable<HTMLElement> = rootRef.current?.closest(
                MUI_POPOVER_PAPER_CLASS_SELECTOR
            ) as Nullable<HTMLElement>
            const anchorEl: Nullable<HTMLElement> = anchorRef?.current as Nullable<HTMLElement>
            if (!paper || !anchorEl) return

            const plusButton: Nullable<HTMLElement> = anchorEl.querySelector(
                FIELDS_ADD_BUTTON_SELECTOR
            ) as Nullable<HTMLElement>
            if (!plusButton) return

            const buttonRect: DOMRect = plusButton.getBoundingClientRect()
            const scrollTop: number = window.pageYOffset || document.documentElement.scrollTop
            const buttonBottom: number = buttonRect.bottom + scrollTop
            const paperHeight: number = paper.offsetHeight
            const desiredTop: number = buttonBottom - paperHeight - POPOVER_VERTICAL_OFFSET_PX
            const top: number = Math.max(scrollTop + POPOVER_MARGIN_THRESHOLD_PX, desiredTop)
            paper.style.top = `${Math.round(top)}px`
        })
    }, [anchorRef])

    const getStatInfo = (id: number): Definable<StatProps> => statsList.find((stat) => stat.StatID === id)

    const handleShowSubMenu = (e: React.MouseEvent<HTMLAnchorElement>, name: string, subItems: AddMenuItem[]): void => {
        e.preventDefault()
        pendingNavRef.current = { subItems, name, isBack: false }
        setSlideDirection(SlideDirectionType.right)
        setSlideIn(false)
    }

    const handleMenuBack = (): void => {
        pendingNavRef.current = { subItems: [], name: EMPTY_STRING, isBack: true }
        setSlideDirection(SlideDirectionType.left)
        setSlideIn(false)
    }

    const handleExited = (): void => {
        const nav: Nullable<PendingNav> = pendingNavRef.current
        if (!nav) return

        if (!nav.isBack) {
            setMenuStack((prev: AddMenuItem[][]): AddMenuItem[][] => [...prev, nav.subItems])
            setBreadcrumb((prev: string[]): string[] => [...prev, nav.name])
            setSlideDirection(SlideDirectionType.left)
        } else {
            setMenuStack((prev: AddMenuItem[][]): AddMenuItem[][] => prev.slice(0, -1))
            setBreadcrumb((prev: string[]): string[] => prev.slice(0, -1))
            setSlideDirection(SlideDirectionType.right)
        }

        pendingNavRef.current = null
        setSlideIn(true)
        repositionPaper()
    }

    const rootMenuItems: AddMenuItem[] = useMemo(
        () => buildMenuStructureFromAvailableStats(language, statsList),
        [language, statsList]
    )

    const currentMenuItems: AddMenuItem[] = useMemo(
        () => (menuStack.length > 0 ? menuStack[menuStack.length - 1] : rootMenuItems),
        [menuStack, rootMenuItems]
    )

    const configuredStatisticsConfigurationsSignature: string = useMemo(
        (): string => configuredStatisticsConfigurations.map((c: StatisticsConfiguration): number => c.ID).join(COMMA),
        [configuredStatisticsConfigurations]
    )

    const buildCurrentView = (): ReactNode[] =>
        currentMenuItems.map((menuItem: AddMenuItem, index: number): ReactNode[] => {
            const key: string = String(index)
            const menuAll: ReactNode[] = []

            if (menuItem?.name) {
                menuAll.push(
                    <Li key={`cat${DASH}${key}`}>
                        <a
                            href='#'
                            onClick={(e: React.MouseEvent<HTMLAnchorElement>): void =>
                                handleShowSubMenu(e, menuItem.name ?? EMPTY_STRING, menuItem.subMenuItems ?? [])
                            }
                            className={`submenu-link ${menuItem?.customClass ?? EMPTY_STRING}`}
                            title={menuItem?.title}
                        >
                            {menuItem?.name}
                            <Icon iconName={IconName.ARROW_RIGHT}></Icon>
                        </a>
                    </Li>
                )
            }

            if (menuItem?.statIds) {
                menuItem.statIds.map((statId: number, i: number): void => {
                    const uid: string = `${key}${DASH}${i}${DASH}${statId}`
                    const statInfo: Definable<StatProps> = getStatInfo(statId)
                    const isRealTimeStat: boolean = statInfo?.IsActiveRTStat ?? false
                    const checked: boolean = !!configuredStatisticsConfigurations.find(
                        (configuredStatisticsConfiguration): boolean =>
                            configuredStatisticsConfiguration.ID === statInfo?.StatID
                    )

                    let licensed: boolean = false
                    if (
                        isUserLicensedForCurrentReportStyle &&
                        rptFilterEntityInfo &&
                        statInfo &&
                        (Number(rptFilterEntityInfo.EntityInfo.ReportCapabilityInfo.StatFlags) &
                            statInfo?.StatFlagsType) >
                            REPORT_STAT_FLAGS_TYPE_NONE
                    ) {
                        licensed = true
                    }

                    menuAll.push(
                        <Li key={`stat${DASH}${uid}`} className={`stat-id${DASH}${statId}`}>
                            <Checkbox
                                labelRight={
                                    isRealTimeStat ? (
                                        <div className='rt-only-label'>
                                            <span>{statInfo?.MediumName}</span>
                                            <Chip
                                                className='rt-only-chip'
                                                size='small'
                                                label={language.get(FieldsListLanguage.FieldsList_RTOnly)}
                                            />
                                        </div>
                                    ) : (
                                        statInfo?.MediumName
                                    )
                                }
                                isChecked={checked}
                                onChange={(): void => {
                                    if (statInfo) {
                                        if (checked) {
                                            removeStatistic(statInfo)
                                        } else {
                                            addStatistic(statInfo)
                                        }
                                    }
                                }}
                                fullWidth
                                disabled={
                                    selectedReportStyle === ReportTypes.CALLS_BY_INTERVAL_AND_ENTITY &&
                                    ArrayUtils.includesAny([statInfo?.StatID ?? EMPTY_STRING], ENTITY_STAT_FIXED_FIELDS)
                                }
                                title={
                                    licensed
                                        ? statInfo?.LongName
                                        : `${language?.get(
                                              FieldsListLanguage.UnlicensedPrefix
                                          )}${SPACE}${statInfo?.LongName}`
                                }
                            />
                        </Li>
                    )

                    return undefined
                })
            }

            return menuAll
        })

    let navTitle: string = language?.get(FieldsListLanguage.FieldsList_AddMenuTitle) ?? EMPTY_STRING

    if (breadcrumb.length > 1) {
        navTitle = breadcrumb.join(`${SPACE}${SLASH}${SPACE}`)
    } else if (breadcrumb.length === 1) {
        navTitle = breadcrumb[0]
    }

    useEffect((): void => {
        repositionPaper()
    }, [repositionPaper, configuredStatisticsConfigurationsSignature])

    return (
        <div ref={rootRef} className='fields-list-add'>
            <div className='fields-list-add-nav'>
                {menuStack.length > 0 && (
                    <IconButton
                        onClick={handleMenuBack}
                        iconName={IconName.ARROW_BACK}
                        type={IconButtonType.BUTTON}
                    ></IconButton>
                )}
                <Heading level={HeadingLevel.THREE} className='title'>
                    {navTitle}
                </Heading>
            </div>
            <Ul className='fields-list-add-menu'>
                {statsList.length > 0 ? (
                    <Slide direction={slideDirection} in={slideIn} appear={false} onExited={handleExited}>
                        <div>{buildCurrentView()}</div>
                    </Slide>
                ) : (
                    <Li>{language.get(FieldsListLanguage.NoFieldsAvailable)}</Li>
                )}
            </Ul>
        </div>
    )
}
export default FieldsListAdd
